import _ from 'lodash'
import { defaultTheme, Text } from '@agro-club/agroclub-shared'
import { isVisibleCredit } from 'env'
import { getDealPaidSum } from 'helpers/getDealPaidSum'
import { DASH } from 'modules/constants'
import { getPriceTypeLabel } from 'modules/domain/bid/utils'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { formatPriceLogistics, getPriceLogisticsLabel } from 'modules/utils/numbers/formatPriceLogistics'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import { formatTarif } from 'modules/utils/numbers/formatTarif'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { Card } from 'views/components/Card/Card'
import { KeyValue } from 'views/components/KeyValue/KeyValue'
import { PriceCarrierBlock } from 'views/pages/Deal/DealDetail/PriceCarrierBlock'
import { FormulaTooltip } from '../../components/FormulaTooltip'
import { hasBaseCreditData } from '../../helpers/hasBaseCreditData'
import { useDealContext } from '../DealContext'
import { formatTurnoverDays } from '../helpers'
import { MapButtons } from '../MappButtons/MapButtons'
import { PriceWithBroker } from './PriceWithBroker'
import { Badge } from 'views/components/Badge/Badge'
import { DealRailwayEditBtn } from '../DealRailwayData/DealRailwayEditBtn'
import { DealType } from 'modules/domain/deal/types'
import { DefaultTooltip } from 'views/components/DefaultTooltip/DefaultTooltip'

const getShipmentTranslationKeys = () => {
  return {
    picked_up: 'fields.loaded',
    delivered: 'fields.uploaded',
  }
}

const isNumber = (value: unknown): value is number => typeof value === 'number'

export const GeneralInfoContent: FC = () => {
  const { t } = useTranslation('deal')
  const { deal, dealRefetch } = useDealContext()

  const freightRateLabel = getPriceLogisticsLabel()

  const hasBaseCredData = hasBaseCreditData(deal)
  const shipmentTranslationKeys = getShipmentTranslationKeys()
  const salePrice = (
    <PriceWithBroker
      price={deal.actual_sale_price}
      commision={deal.seller_broker ? deal.seller_broker.commission : undefined}
      sign="+"
    />
  )
  const purchasePrice = (
    <PriceWithBroker
      price={deal.actual_purchase_price}
      commision={deal.customer_broker ? deal.customer_broker.commission : undefined}
      sign="-"
    />
  )

  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })

  let badgeColor: any = 'green'
  let logisticTagText = t('address:overLoadTypes.normal')
  if (deal.is_using_overload) {
    badgeColor = 'purple'
    logisticTagText = t('address:overLoadTypes.full')
  }

  return (
    <Card.Grid cols={isMobile ? 2 : 3} maxColumnWidth={isMobile ? '' : '200px'} columnGap={8}>
      <KeyValue label={t('fields.quantityShort')}>{formatQuantity(deal.actual_quantity)}</KeyValue>
      {!isMobile && (
        <KeyValue label={t('fields.distance')}>
          <MapButtons deal={deal} />
        </KeyValue>
      )}
      <div></div>
      {isMobile && <MapButtons deal={deal} isMobile />}
      <KeyValue label={getPriceTypeLabel(deal.sale_bid.price_type, t)}>{salePrice}</KeyValue>
      <KeyValue label={getPriceTypeLabel(deal.purchase_bid.price_type, t)}>{purchasePrice}</KeyValue>
      <KeyValue label={t('fields.price_with_logistics')}>{formatTarif(deal.price_with_logistics)}</KeyValue>
      {deal.deal_type !== DealType.railway && (
        <KeyValue label={freightRateLabel}>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <FormulaTooltip title={t('formulaWarning')} numberFormula={deal.price_logistics_formula}>
              {formatPriceLogistics(deal.actual_price_logistics)}
            </FormulaTooltip>
            <Badge color={badgeColor}>{logisticTagText}</Badge>
          </div>
        </KeyValue>
      )}
      {deal.deal_type === DealType.railway && (
        <KeyValue label={freightRateLabel}>
          <div style={{ display: 'flex' }}>
            <div>
              <div style={{ marginBottom: '4px' }}>
                {isNumber(deal.deal_railway_data?.logistics_price)
                  ? formatPriceLogistics(deal.deal_railway_data.logistics_price)
                  : DASH}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <DefaultTooltip tooltipContent={t('Затраты элеватора')}>
                  <Text typography="bodySmall" color="secondary">
                    {isNumber(deal.deal_railway_data?.all_costs)
                      ? formatPriceLogistics(deal.deal_railway_data.all_costs)
                      : DASH}
                  </Text>
                </DefaultTooltip>
                <DefaultTooltip tooltipContent={t('Ж/Д тариф')}>
                  <Text typography="bodySmall" color="secondary">
                    {deal.deal_railway_data?.rate ? formatPriceLogistics(deal.deal_railway_data.rate) : DASH}
                  </Text>
                </DefaultTooltip>
              </div>
              <DefaultTooltip tooltipContent={t('Авто до элеватора')}>
                <Text typography="bodySmall" color="secondary">
                  {isNumber(deal.deal_railway_data?.farmer_transit_costs)
                    ? formatPriceLogistics(deal.deal_railway_data.farmer_transit_costs)
                    : DASH}
                </Text>
              </DefaultTooltip>
            </div>
            <div style={{ marginTop: -6 }}>
              <DealRailwayEditBtn deal={deal} refetch={dealRefetch} />
            </div>
          </div>
        </KeyValue>
      )}
      <PriceCarrierBlock priceCarrier={deal.price_carrier} />
      <KeyValue label={deal.fixed_margin_by_export_contract ? t('export:contractPrice') : t('fields.fixed_margin')}>
        {formatPrice(deal?.fixed_margin ?? '0', true)}
      </KeyValue>
      <KeyValue label={t('fields.paid_sum')}>{getDealPaidSum(deal)}</KeyValue>
      {!!deal.loaded_quantity && (
        <KeyValue label={t(shipmentTranslationKeys.picked_up)}>{formatQuantity(deal.loaded_quantity)}</KeyValue>
      )}
      {!!deal.delivered_quantity && (
        <KeyValue
          label={
            deal.has_commission_trips ? t('fields.uploaded_with_committent') : t(shipmentTranslationKeys.delivered)
          }
        >
          {formatQuantity(deal.delivered_quantity)}
        </KeyValue>
      )}
      {isVisibleCredit && (
        <>
          <KeyValue label={t('turnoverDaysPlan')}>
            {hasBaseCredData ? formatTurnoverDays(deal.turnover_days) : DASH}
          </KeyValue>
          <KeyValue label={t('turnoverDaysActual')}>
            {hasBaseCredData ? formatTurnoverDays(deal.actual_turnover_days) : DASH}
          </KeyValue>
        </>
      )}
    </Card.Grid>
  )
}
